import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Copy, Check, ExternalLink, AlertTriangle, Calendar, Key, Mail, Globe } from 'lucide-react';
import type { OnboardTenantResponse } from '@yawi/types';
import { Modal, Button } from '@/core/ui/components';

interface TenantCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: OnboardTenantResponse;
}

export function TenantCreatedModal({ isOpen, onClose, result }: TenantCreatedModalProps) {
  const navigate = useNavigate();
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(result.adminCredentials.password);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(result.urls.subdomain);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleViewTenant = () => {
    onClose();
    navigate(`/tenants/${result.tenant.id}`);
  };

  const handleGoToList = () => {
    onClose();
    navigate('/tenants');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleGoToList}
      title=""
      size="lg"
    >
      <div className="text-center py-4">
        {/* Success Icon */}
        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
        </div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Tenant Creado Exitosamente
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {result.tenant.name} ha sido configurado y está listo para usar
        </p>
      </div>

      {/* Tenant Info */}
      <div className="space-y-4 mb-6">
        {/* URL */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">URL del Tenant</p>
                <p className="font-medium text-gray-900 dark:text-white">{result.urls.subdomain}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopyUrl}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Copiar URL"
              >
                {copiedUrl ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
              </button>
              <a
                href={result.urls.subdomain}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Abrir en nueva pestaña"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Admin Email */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Mail className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Email del Administrador</p>
              <p className="font-medium text-gray-900 dark:text-white">{result.adminCredentials.email}</p>
            </div>
          </div>
        </div>

        {/* Password - CRITICAL */}
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Key className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">Contraseña del Administrador</p>
                <p className="font-mono text-lg font-bold text-gray-900 dark:text-white tracking-wider">
                  {result.adminCredentials.password}
                </p>
              </div>
            </div>
            <button
              onClick={handleCopyPassword}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                copiedPassword
                  ? 'bg-emerald-500 text-white'
                  : 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 hover:bg-amber-300 dark:hover:bg-amber-700'
              }`}
            >
              {copiedPassword ? (
                <>
                  <Check className="w-4 h-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copiar
                </>
              )}
            </button>
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800 dark:text-red-200">
              Guarda estas credenciales ahora
            </p>
            <p className="text-sm text-red-600 dark:text-red-300 mt-1">
              Esta contraseña no se puede recuperar después. Asegúrate de guardarla en un lugar seguro o enviarla al administrador.
            </p>
          </div>
        </div>

        {/* Subscription Info */}
        {result.subscription && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">Suscripción</p>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                    result.subscription.status === 'TRIAL'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  }`}>
                    {result.subscription.status === 'TRIAL' ? 'Prueba' : result.subscription.status}
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {result.subscription.billingCycle}
                  </span>
                </div>
                {result.subscription.trialEndsAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Prueba termina: {formatDate(result.subscription.trialEndsAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" onClick={handleGoToList} className="flex-1">
          Ir a Tenants
        </Button>
        <Button onClick={handleViewTenant} className="flex-1">
          Ver Detalle
        </Button>
      </div>
    </Modal>
  );
}
